import { $, addAction, doAction } from '@situation/setdesign.util';
import { HOOKS, selectors } from '../constants';
import FormView from '../views/FormView';

/**
 * Controller for syncing Gravity Form inputs with application state.
 */
export default class FormController {
    /**
     * @param {import('../state/StateManager').default} stateManager
     */
    constructor(stateManager) {
        this.stateManager = stateManager;
        this.formClass = selectors.formClass;
        this.view = new FormView({
            formSelector: selectors.form,
            onDateClick: this.handleDateClick.bind(this),
            onSectionChange: this.handleSectionChange.bind(this),
            onPeopleChange: this.handlePeopleChange.bind(this),
        });

        this._pendingPrefIndex = null;
    }

    /**
     * Sets up event hooks and listeners for Gravity Form renders.
     */
    init() {
        // React to user selecting an event in the calendar
        addAction(HOOKS.PREF_EVENT_SELECTED, (eventID) => this.applyEventSelection(eventID));
        // When preferences update in state, re-render the corresponding row
        addAction(HOOKS.PREF_UPDATED, (index, preference) => {
            this.view.updatePreferenceRow(index, preference);
        });

        // Listen for changes to star price selection status
        addAction(HOOKS.STAR_PRICE_STATUS_CHANGED, (hasStarPrice) => {
            this.view.toggleStarPriceClass(hasStarPrice);
        });

        // Listen for ANY Gravity Form render on the page.
        document.addEventListener('gform/post_render', this.onFormRender.bind(this));

        // In case the form is already rendered before this script runs,
        // check and trigger the handler manually.
        if (document.querySelector(selectors.form)) {
            this.onFormRender();
        }
    }

    /**
     * Handles the `gform_post_render` event to re-initialize the view.
     * @param {Event} [event] - The event object from the listener.
     */
    onFormRender(event) {
        // If triggered by an event, ensure it's our form.
        if (event && event.detail) {
            const { formId } = event.detail;
            const $form = $(`#gform_${formId}`);
            if (!$form.hasClass(this.formClass)) {
                return; // Not our form, do nothing.
            }
        }

        // 1. Set up the layout (this is idempotent)
        this.view.setupLayout();
        // 2. Re-bind all listeners and cache elements
        this.view.bindUI();
        // 3. Re-apply the current state to the new DOM
        Object.entries(this.stateManager.preferences).forEach(([index, preference]) => {
            this.view.updatePreferenceRow(Number(index), preference);
        });

        // 4. Apply initial star price class state
        this.view.toggleStarPriceClass(this.stateManager.hasStarPrice);
    }

    /**
     * When a date field is clicked in the form, open calendar modal and remember index.
     * @param {number} prefIndex
     */
    handleDateClick(prefIndex) {
        this._pendingPrefIndex = prefIndex;
        doAction('showModal', $(selectors.calendarModal));
    }

    /**
     * When a seating section is changed, update state.
     * @param {number} prefIndex
     * @param {string} section
     */
    handleSectionChange(prefIndex, section) {
        this.stateManager.updatePreference(prefIndex, { section });
    }

    /**
     * When the number of people changes, update state.
     * @param {number} prefIndex
     * @param {number} people
     */
    handlePeopleChange(prefIndex, people) {
        this.stateManager.updatePreference(prefIndex, { people });
    }

    /**
     * Applies a calendar event selection to the pending preference index.
     * @param eventID
     */
    applyEventSelection(eventID) {
        const eventObj = this.stateManager.getEventByID(eventID);
        if (this._pendingPrefIndex == null || !eventObj) return;

        this.stateManager.updatePreference(this._pendingPrefIndex, { event: eventObj });
        this._pendingPrefIndex = null;
        doAction('hideModal', $(selectors.calendarModal));
    }
}
